import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {ChangeEvent, KeyboardEvent, MouseEvent} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import type {
    ColDef,
    GetRowIdParams,
    GridApi,
    GridReadyEvent,
    ICellRendererParams,
    IRowNode,
    SuppressHeaderKeyboardEventParams
} from 'ag-grid-community';
import {CircleQuestionMark} from 'lucide-react';
import {AppGrid} from 'components/common/ag-grid/AppGrid.component';
import {useAvailableHeight} from 'components/common/ag-grid/useAvailableHeight';
import Checkbox from 'components/common/checkbox.component';
import {ICON_SIZE, KEY} from 'components/common/constants';
import {Tooltip} from 'components/tooltip/Tooltip';
import {HEADING_INFO_TOOLTIP_PLACE, SHARED_TOOLTIP_ID} from 'components/tooltip/AppTooltip.component';
import {useModalContext} from 'components/modals/ModalContext';
import {useTranslation} from 'components/translations/useTranslation';
import {launchDarkly} from 'utils/launchDarkly';
import {MODAL_ACTIONS, MODAL_SEVERITY} from 'utils/constants';
import type {Translate} from 'types/myTasks';
import type {Department, DepartmentApprover, DepartmentsProps} from 'types/departments';
import type {DepartmentGridRow} from 'types/newDepartments';
import * as reduxSelectors from '../store/application.reducers';
import UserSearch from '../input/UserSearch.component';
import SearchGroupInput from '../input/SearchGroupInput.component';
import Departments from './Departments.component';
import {filterChanged, getDepartments, saveDepartmentChanges} from './departments.action';
import './newDepartments.scss';

// swaps the classic list in Departments.component for the ag-grid rendering below
const AG_GRID_IN_CONFIGURATION_FLAG = 'fe-use-ng-grid-in-configuration';

// the columns the grid draws, in the order the classic list had them
const NAME_COL = 'name';
const APPROVERS_COL = 'approvers';

// the grid fills the page, keeping the card's own padding and the page's bottom margin below it
const GRID_HEIGHT = Object.freeze({bottomGap: 48, watchSelector: '.new-departments__card'});

// the tab order below is these, in the order the page draws them
const SORTABLE_HEADERS = '.ag-header-cell-sortable';
/**
 * The approver pickers, which are what a row offers the keyboard. A picker standing in front of
 * someone who may not edit renders its input disabled, and a disabled input is no tab stop.
 */
const ROW_CONTROLS = '.new-departments__approvers input:not(:disabled)';

/** The id of a row's approver picker, so a label can name it and the tab order can find it. */
const pickerId = (rowId: string) => `departments-approvers-${rowId}`;

// how many frames a row is given to be drawn before focus gives up on it (see focusPicker)
const FOCUS_RENDER_ATTEMPTS = 5;

/** The row a control stands in, as ag-grid numbers the rows it has drawn. */
function rowIndexOf(element: HTMLElement): number | null {
    const index = element.closest('.ag-row')?.getAttribute('row-index');
    return index === null || index === undefined ? null : Number(index);
}

/**
 * The next row from `from`, walking in `step`, that has a picker to stand on. A department the
 * master data has deleted renders none, so it is no tab stop and is walked over. Null once there
 * is nothing further that way - which is where focus should leave the grid.
 */
function rowToFocus(api: GridApi<DepartmentGridRow>, from: number, step: number): IRowNode<DepartmentGridRow> | null {
    for (let index = from; index >= 0 && index < api.getDisplayedRowCount(); index += step) {
        const node = api.getDisplayedRowAtIndex(index);
        if (node?.data && !node.data.deleted) {
            return node;
        }
    }
    return null;
}

/**
 * Takes Tab off the heading the tab order leaves the header row from - the name, the only one
 * that sorts and so the only one worth standing on. The grid would walk on to the approvers
 * heading, which does nothing, and then loop back to the first, which is a ring focus never gets
 * out of; onGridKeyDown carries it into the rows instead. Shift-Tab is left alone - walking back
 * up the headings is what it should do.
 */
const tabOutOfTheHeaderIsNotTheGrids = ({event}: SuppressHeaderKeyboardEventParams<DepartmentGridRow>) =>
    event.key === KEY.TAB && !event.shiftKey;

/**
 * Every key struck inside an approver cell belongs to the picker standing in it - the arrows that
 * walk its options, Enter and Escape that take and drop them, and Tab, which onGridKeyDown
 * answers. Left to the grid, they would be read as navigation behind the picker's back.
 */
const theCellOwnsEveryKey = () => true;

/**
 * The rows the grid draws: the departments that have a name, and - only when the page is asked
 * for them - the ones the master data has since deleted.
 */
function buildRowData(departments: Department[], showDeleted: boolean, translate: Translate): DepartmentGridRow[] {
    return departments
        .filter(department => department.name && (!department.mdmDeleted || showDeleted))
        .map(department => {
            const deleted = !!department.mdmDeleted;
            const label = `${department.name} (${department.organizationUnitId})`;

            return {
                id: `${department.internalOrgUnitId}-${department.organizationUnitId}`,
                internalOrgUnitId: department.internalOrgUnitId,
                name: department.name,
                label: deleted ? `${label} ${translate('departments.deleted')}` : label,
                deleted,
                managers: department.managers
            };
        });
}

/**
 * ag-grid rendering of the Configuration -> Departments page, behind
 * fe-use-ng-grid-in-configuration. The classic list it replaces is in Departments.component.
 */
export const NewDepartments = (props: DepartmentsProps) => {

    const {translate} = useTranslation();
    const dispatch = useDispatch();
    // every popup this page opens is the modal router's
    const toggleModal = useModalContext()?.toggleModal;

    const currentContextId = useSelector(reduxSelectors.getUsersCurrentContextId);
    const userRoles = useSelector(reduxSelectors.getUsersRoles);
    const allDepartments: Department[] = useSelector(reduxSelectors.getDepartments);
    const filter: string = useSelector(reduxSelectors.getDepartmentsFilter);
    const possibleManagers: DepartmentApprover[] = useSelector(reduxSelectors.getPossibleDepartmentManagers);

    const [showDeletedDepartments, setShowDeletedDepartments] = useState(false);

    // the grid owns its scrolling area, so it is given the height actually left below it
    const [gridElement, setGridElement] = useState<HTMLDivElement | null>(null);
    const gridHeight = useAvailableHeight(gridElement, GRID_HEIGHT);

    const gridApi = useRef<GridApi<DepartmentGridRow> | null>(null);
    const onGridReady = useCallback((event: GridReadyEvent<DepartmentGridRow>) => {
        gridApi.current = event.api;
    }, []);

    const isNavigationBlocked = !!props.blocker?.isNavigationBlocked();

    // seeded from the first render, so the prompt belongs to a navigation the user started
    // here and not to a page that was already being left when it mounted
    const wasNavigationBlocked = useRef(isNavigationBlocked);

    useEffect(() => {
        dispatch(getDepartments(currentContextId));
    }, [currentContextId]); // eslint-disable-line react-hooks/exhaustive-deps -- reload only when the company changes

    const remain = () => {
        props.blocker?.blocker?.reset?.();
    };

    const leave = () => {
        props.blocker?.blocker?.proceed?.();
    };

    useEffect(() => {
        if (isNavigationBlocked && !wasNavigationBlocked.current) {
            toggleModal?.(MODAL_ACTIONS.CONFIRM_ACTION, {
                title: translate('popUp.unsavedChangesTitle'),
                description: translate('settings.confirmPageLeave'),
                confirmButtonText: 'popUp.stay',
                cancelButtonText: 'popUp.leave',
                severity: MODAL_SEVERITY.INFORMATION,
                handleAction: () => {
                    toggleModal?.(null);
                    remain();
                },
                closeCallback: () => leave()
            });
        }

        wasNavigationBlocked.current = isNavigationBlocked;
    });

    const save = useCallback((departmentId: number, values: DepartmentApprover | DepartmentApprover[]) => {
        const departmentManagerIds = (Array.isArray(values) ? values : [values])
            .filter(approver => approver.value)
            .map(approver => approver.value);

        dispatch(saveDepartmentChanges({departmentId, departmentManagerIds}, currentContextId));
    }, [dispatch, currentContextId]);

    const filterDepartments = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        dispatch(filterChanged(event.target.value));
    }, [dispatch]);

    const removeFilter = useCallback(() => {
        dispatch(filterChanged(''));
    }, [dispatch]);

    const toggleDeletedDepartments = useCallback((event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        event.preventDefault();
        setShowDeletedDepartments(shown => !shown);
    }, []);

    /**
     * The approvers a picker can offer. Read off a ref rather than closed over: the selector
     * behind them maps a fresh list on every call, and closing over that would rebuild the column
     * definitions - and with them every picker - on each render.
     */
    const approverOptions = useRef(possibleManagers);
    approverOptions.current = possibleManagers;

    // the departments arrive already searched over, so the rows only drop the nameless ones and,
    // unless they are asked for, the deleted
    const rowData = useMemo(
        () => buildRowData(allDepartments, showDeletedDepartments, translate),
        [allDepartments, showDeletedDepartments, translate]
    );

    /**
     * Focuses the picker of a department the grid may not have drawn yet: the rows are
     * virtualised, so one that has just been scrolled to is only in the document a frame or more
     * later.
     */
    const focusPicker = useCallback((rowId: string, attempts = FOCUS_RENDER_ATTEMPTS) => {
        const picker = document.getElementById(pickerId(rowId));
        if (picker) {
            picker.focus();
        } else if (attempts > 0) {
            requestAnimationFrame(() => focusPicker(rowId, attempts - 1));
        }
    }, []);

    /**
     * The tab order inside the grid: the heading that sorts - the name alone - and then the
     * approver picker each row carries, every department in the list rather than only the ones
     * on screen.
     *
     * What the grid and the browser would do between them is walk every heading, the approvers
     * one that does not sort included, in a ring, and stop on each row before the picker it
     * carries - the row is a tab stop of the grid's own making (see AppGrid), and standing on one
     * does nothing. Every step below is one of those skipped: the last sortable heading hands
     * focus to the first picker, and a row's picker hands it to the picker of the row under it.
     */
    const onGridKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== KEY.TAB) {
            return;
        }

        const grid = event.currentTarget;
        const controls = Array.from(grid.querySelectorAll<HTMLElement>(ROW_CONTROLS));
        if (controls.length === 0) {
            return;
        }

        const headers = Array.from(grid.querySelectorAll<HTMLElement>(SORTABLE_HEADERS));
        const lastHeader = headers[headers.length - 1];
        const target = event.target as HTMLElement;

        const moveTo = (element: HTMLElement | null | undefined) => {
            if (!element) {
                return;
            }
            element.focus();
            event.preventDefault();
        };

        if (target === lastHeader && !event.shiftKey) {
            moveTo(controls[0]);
            return;
        }

        const drawn = controls.indexOf(target);
        if (drawn === -1) {
            return;
        }

        const step = event.shiftKey ? -1 : 1;

        // among the rows the grid has drawn, the next picker is simply the one beside this
        const neighbour = controls[drawn + step];
        if (neighbour) {
            moveTo(neighbour);
            return;
        }

        /*
         * Off the end of what is drawn, which is rarely the end of the list: the grid virtualises
         * its rows, so the department to go to next is often one it has not drawn - and a picker
         * that is not in the document is one the browser would tab straight past, carrying focus
         * out of the grid and leaving every department below out of the keyboard's reach. The
         * grid is asked to scroll that row in, and focus follows it there once it is drawn.
         */
        const api = gridApi.current;
        const from = rowIndexOf(target);
        const next = api && from !== null ? rowToFocus(api, from + step, step) : null;

        if (api && next?.data && next.rowIndex !== null) {
            api.ensureIndexVisible(next.rowIndex);
            focusPicker(next.data.id);
            event.preventDefault();
            return;
        }

        // nothing further that way: back up into the headings, or on out of the grid altogether
        if (event.shiftKey) {
            moveTo(lastHeader);
        }
    }, [focusPicker]);

    const columnDefs = useMemo<ColDef<DepartmentGridRow>[]>(() => {
        const nameLabel = translate('departments.departments');
        const approversLabel = translate('departments.departmentApprovers');
        // only a system administrator assigns approvers; everyone else reads the list
        const readOnly = !userRoles.systemAdministrator;

        return [{
            colId: NAME_COL,
            field: 'name',
            headerName: nameLabel,
            // a narrowed column clips its heading; the tooltip is what spells it out again
            headerTooltip: nameLabel,
            // the proportions the classic list gave its two columns with col-md-4 / col-md-8
            flex: 4,
            minWidth: 160,
            // the list arrives ordered by name, and the third click on the heading returns it there
            sortable: true,
            suppressHeaderKeyboardEvent: tabOutOfTheHeaderIsNotTheGrids,
            // ag-grid cannot spot truncation inside a cell renderer, so the tooltip is asked for
            // explicitly
            tooltipValueGetter: params => params.data?.label || undefined,
            cellRenderer: (params: ICellRendererParams<DepartmentGridRow>) => {
                const row = params.data;
                if (!row) {
                    return null;
                }
                return (
                    <span className={row.deleted ? 'new-departments__name text-danger' : 'new-departments__name'}>
                        {row.label}
                    </span>
                );
            }
        }, {
            colId: APPROVERS_COL,
            headerName: approversLabel,
            headerTooltip: approversLabel,
            flex: 8,
            minWidth: 220,
            // the cell holds a picker, not a value: there is nothing here to order the rows by
            sortable: false,
            // a department with several approvers wears a taller picker, and the row grows with it
            autoHeight: true,
            suppressKeyboardEvent: theCellOwnsEveryKey,
            cellRenderer: (params: ICellRendererParams<DepartmentGridRow>) => {
                const row = params.data;
                // a department the master data has deleted has nothing left to assign
                if (!row || row.deleted) {
                    return null;
                }
                const id = pickerId(row.id);
                return (
                    <span className="new-departments__approvers">
                        {/* one of many pickers on the page, so it says which department it belongs to */}
                        <label className="new-departments__picker-label" htmlFor={id}>
                            {`${approversLabel} - ${row.name}`}
                        </label>
                        <UserSearch id={id}
                                    propagateValue={(value: DepartmentApprover | DepartmentApprover[]) =>
                                        save(row.internalOrgUnitId, value)}
                                    value={row.managers}
                                    options={approverOptions.current}
                                    stopFocus={true}
                                    disabled={readOnly}
                                    multi={true}
                                    menuPosition="fixed"
                                    // the rows are moved about with a transform, which a menu
                                    // drawn inside one would be cut off by
                                    menuPortalTarget={document.body}
                                    translate={translate}/>
                    </span>
                );
            }
        }];
    }, [translate, userRoles.systemAdministrator, save]);

    return (
        // the outer wrapper is what ConfigurationWrapper used to render around every configuration page
        <div>
            <div className="w-100">
                <main className="page w-100 d-flex justify-content-center align-items-start">
                    <div className="py-16 mb-48 w-75 settings-page__container">
                        <h2 className="ps-4 mb-4 d-flex align-items-center">{translate("departments.header")}
                            <span className="ms-4"
                                  data-for={SHARED_TOOLTIP_ID}
                                  data-place={HEADING_INFO_TOOLTIP_PLACE}
                                  data-tip="departments-header-info-tooltip">
                                <CircleQuestionMark size={ICON_SIZE.MEDIUM}/>
                            </span>
                            <Tooltip
                                id="departments-header-info-tooltip"
                                content={() => translate("departments.headerInfo")}
                            />
                        </h2>

                        <div className="bg-white radius-4 p-4 setting_collapsable-main new-departments__card">
                            <div className="d-flex justify-content-end gap-3 align-items-center mb-24">
                                {/* the Checkbox label carries w-100, so it needs a shrink-to-fit wrapper to not stretch the row */}
                                <div className="d-flex align-items-center text-nowrap">
                                    <Checkbox
                                        inputId={"showDeletedDepartments"}
                                        onChange={toggleDeletedDepartments}
                                        isChecked={showDeletedDepartments}
                                        ariaLabel={translate("departments.showDeleted")}
                                        label={translate("departments.showDeleted")}
                                    />
                                </div>
                                <SearchGroupInput onInputChange={filterDepartments}
                                                  value={filter}
                                                  className="ga-input__default"
                                                  onClearInput={removeFilter}
                                                  translate={translate}/>
                            </div>

                            <AppGrid<DepartmentGridRow>
                                className="new-departments__grid"
                                dataCy="departments.grid"
                                containerRef={setGridElement}
                                height={gridHeight}
                                rowData={rowData}
                                columnDefs={columnDefs}
                                getRowId={(params: GetRowIdParams<DepartmentGridRow>) => params.data.id}
                                onGridReady={onGridReady}
                                onKeyDown={onGridKeyDown}
                                localeText={{noRowsToShow: translate('departments.noMatches')}}
                            />
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

/**
 * Configuration -> Departments. The flag is read here rather than around the route, so the page
 * itself decides which of its two renderings it is.
 */
export const DepartmentsWrapper = (props: DepartmentsProps) => {
    const useAgGrid = launchDarkly.userClient?.variation(AG_GRID_IN_CONFIGURATION_FLAG, false) ?? false;

    return useAgGrid ? <NewDepartments {...props}/> : <Departments {...props}/>;
};

export default DepartmentsWrapper;
